import {
  getResolvedShots,
  getShotsOnTargetPercentage,
  SHOTS_ON_TARGET_PERCENT_METRIC_KEY,
  SHOTS_METRIC_KEY,
  toFiniteMetricNumber,
} from "./shotsMetrics";

export interface PlayerColumn {
  id: string;
  label: string;
  fullLabel: string;
  isShow: boolean;
}

export interface PlayerSort {
  columnId: string;
  direction: "asc" | "desc";
}

interface PlayerStatItem {
  params?: Record<string, unknown>[];
}

interface TeamPlayersData {
  players_stat?: PlayerStatItem[];
}

interface PlayersData {
  home_team?: TeamPlayersData;
  away_team?: TeamPlayersData;
}

const LEGACY_PLAYER_COLUMN_LABELS: Record<string, string> = {
  Minutes: "MINS",
  Goals: "G",
  "Own goals": "OG",
  Assists: "AST",
  YC: "YC",
  RC: "RC",
  Shots: "SH",
  "Shots on target": "SH+",
  "Shots on target, %": "SH%",
  Offsides: "OFF",
  Fouls: "F",
  "Fouls on": "FS",
};

const COMMON_DYNAMIC_PLAYER_COLUMN_LABELS: Record<string, string> = {
  "Blocked shot": "BS",
  Duel: "DU",
  Interception: "INT",
  Save: "SV",
  "Wide shot": "WS",
  "Shot into the bar/post": "SBP",
  Penalties: "PEN",
  "Penalties scored": "P+",
  "Penalties missed": "P-",
};

const LEGACY_PLAYER_COLUMN_ORDER = [
  "Minutes",
  "Goals",
  "Own goals",
  "Assists",
  "YC",
  "RC",
  "Shots",
  "Shots on target",
  "Shots on target, %",
  "Offsides",
  "Fouls",
  "Fouls on",
];

const BASE_METRICS_MIN_WIDTH = 672;
export const PLAYER_METRIC_COLUMN_WIDTH = 56;
export const PLAYER_LOGO_COLUMN_WIDTH = 35;
export const PLAYER_INFO_COLUMN_WIDTH = 250;
export const PLAYER_SIDE_PADDING = 15;
export const PLAYER_FIXED_BLOCK_WIDTH =
  PLAYER_LOGO_COLUMN_WIDTH + PLAYER_INFO_COLUMN_WIDTH;

const addMetricKeys = (
  playersStat: PlayerStatItem[] | undefined,
  keys: string[],
  seenKeys: Set<string>
) => {
  if (!Array.isArray(playersStat)) {
    return;
  }

  playersStat.forEach((player) => {
    const playerParams = player?.params?.[0];
    if (!playerParams || typeof playerParams !== "object") {
      return;
    }

    Object.keys(playerParams).forEach((metricKey) => {
      if (seenKeys.has(metricKey)) {
        return;
      }
      seenKeys.add(metricKey);
      keys.push(metricKey);
    });
  });
};

export const getPlayerColumnLabel = (metricKey: string) =>
  LEGACY_PLAYER_COLUMN_LABELS[metricKey] || COMMON_DYNAMIC_PLAYER_COLUMN_LABELS[metricKey];

const buildFallbackAbbreviationBase = (metricKey: string) => {
  const words = metricKey
    .toUpperCase()
    .match(/[A-Z0-9]+/g);

  if (!words || words.length === 0) {
    return metricKey.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 3) || "COL";
  }

  if (words.length === 1) {
    return words[0].slice(0, 3) || "COL";
  }

  return words
    .map((word) => word[0])
    .join("")
    .slice(0, 4);
};

const buildDynamicAbbreviationMap = (metricKeys: string[]) => {
  const usedLabels = new Set<string>();
  const dynamicLabels: Record<string, string> = {};

  metricKeys.forEach((metricKey) => {
    const knownLabel = getPlayerColumnLabel(metricKey);
    if (knownLabel) {
      dynamicLabels[metricKey] = knownLabel;
      usedLabels.add(knownLabel);
    }
  });

  metricKeys.forEach((metricKey) => {
    if (dynamicLabels[metricKey]) {
      return;
    }

    const baseLabel = buildFallbackAbbreviationBase(metricKey);
    let nextLabel = baseLabel;
    let suffix = 2;

    while (usedLabels.has(nextLabel)) {
      nextLabel = `${baseLabel}${suffix}`;
      suffix += 1;
    }

    dynamicLabels[metricKey] = nextLabel;
    usedLabels.add(nextLabel);
  });

  return dynamicLabels;
};

export const buildPlayerColumns = (
  playersData: PlayersData,
  previousColumns: PlayerColumn[] = []
) => {
  const discoveredKeys: string[] = [];
  const seenKeys = new Set<string>();
  const legacyKeys = new Set(LEGACY_PLAYER_COLUMN_ORDER);

  addMetricKeys(playersData.home_team?.players_stat, discoveredKeys, seenKeys);
  addMetricKeys(playersData.away_team?.players_stat, discoveredKeys, seenKeys);

  const orderedLegacyKeys = LEGACY_PLAYER_COLUMN_ORDER.filter((key) =>
    seenKeys.has(key)
  );
  const orderedDynamicKeys = discoveredKeys.filter((key) => !legacyKeys.has(key));
  const orderedKeys = [...orderedLegacyKeys, ...orderedDynamicKeys];

  if (orderedKeys.length === 0) {
    return previousColumns;
  }

  const previousColumnsById = new Map(
    previousColumns.map((column) => [column.id, column])
  );
  const columnLabels = buildDynamicAbbreviationMap(orderedKeys);

  return orderedKeys.map((id) => {
    const previousColumn = previousColumnsById.get(id);
    return {
      id,
      label: columnLabels[id] || id,
      fullLabel: id,
      isShow: previousColumn?.isShow ?? true,
    };
  });
};

export const getPlayerMetricNumber = (
  playerParams: Record<string, unknown> | undefined,
  metricKey: string
) => {
  if (metricKey === SHOTS_METRIC_KEY) {
    return getResolvedShots(playerParams);
  }

  if (metricKey === SHOTS_ON_TARGET_PERCENT_METRIC_KEY) {
    return getShotsOnTargetPercentage(playerParams);
  }

  return toFiniteMetricNumber(playerParams?.[metricKey]);
};

export const getMetricsMinWidth = (columns: PlayerColumn[]) =>
  Math.max(columns.length * PLAYER_METRIC_COLUMN_WIDTH, BASE_METRICS_MIN_WIDTH);
